import {Checkbox, LoadingBox, Radio} from 'letrus-ui';
import {ForwardedRef, forwardRef, useEffect, useState} from 'react';
import {FontAwesomeIcon as Icon} from '@fortawesome/react-fontawesome';
import styles from './Choice.module.scss';

export interface ChoiceProps
  extends React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  > {
  text: string;
  type: string;
  checked?: boolean;
  feedback?: string;
  isCorrect?: boolean;
  isLoading?: boolean;
  hasBeenAnswered?: boolean;
  hasCorrectAnswer?: boolean;
}

function Choice(
  {
    text,
    checked,
    feedback,
    isLoading,
    isCorrect,
    type = 'radio',
    hasBeenAnswered,
    hasCorrectAnswer,
    ...radioProps
  }: ChoiceProps,
  ref: ForwardedRef<HTMLInputElement>,
) {
  const [checkState, setCheckState] = useState(checked);

  const InputElement = type === 'radio' ? Radio : Checkbox;
  const showStatus = hasBeenAnswered && hasCorrectAnswer;

  const statusClass = styles[isCorrect ? 'correct' : 'incorrect'];
  const choiceClass = showStatus ? statusClass : '';

  useEffect(() => {
    setCheckState(!!checked);
  }, [checked]);

  return isLoading ? (
    <LoadingBox width="100%" height="5rem" borderRadius={8} />
  ) : (
    <div className={styles.wrapper}>
      <div
        className={`${styles.choice} ${choiceClass}`}
        data-testid="choice-status"
      >
        <InputElement
          {...radioProps}
          ref={ref}
          label={text}
          checked={checkState}
          className={styles.checkbox}
          onChange={(event) => {
            radioProps.onChange?.(event);
            setCheckState(event.target.checked);
          }}
        />

        <Icon
          size="lg"
          className={styles.icon}
          icon={['fas', isCorrect ? 'circle-check' : 'circle-xmark']}
        />
      </div>

      {hasBeenAnswered && feedback && (
        <p
          aria-describedby={`${radioProps.name}-${radioProps.value}`}
          className={styles.feedback}
        >
          {feedback}
        </p>
      )}
    </div>
  );
}

export default forwardRef(Choice);
